import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { HiOutlineSearch, HiOutlineClock } from "react-icons/hi";
import { toast } from "sonner";

import DashboardLayout from "layouts/dashboard/DashboardLayout";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { useDashboardHome } from "hooks/useDashboardHome";
import { ActionCard, StatCard } from "views/dashboard/dashboardHomeShared";
import {
  type NewApplicationListItem,
  type NewApplicationListItemMark,
} from "api/applications";
import {
  listPendingItemsByModule,
  PENDING_ITEMS_MODULE_OPTIONS,
  type PendingItemsModule,
} from "api/pendingItemsByModule";
import { getPathForLeadByModule } from "utils/pathForLeadByModule";
import {
  formatStatusMarkChipLabel,
  resolveStatusMarkPath,
} from "utils/statusMarkRouting";

import statTrend from "assets/svg/dashboard-stat-trend.svg";
import statDocument from "assets/svg/dashboard-stat-document.svg";
import statChart from "assets/svg/dashboard-stat-chart.svg";
import actionReports from "assets/svg/dashboard-action-reports.svg";
import actionUpdate from "assets/svg/dashboard-action-update.svg";

const PAGE_SIZE = 14;

function formatMarkDisplay(row: NewApplicationListItem): string {
  return row.markDisplay?.trim() || row.markName?.trim() || "—";
}

function formatCreatedAt(isoString: string): string {
  try {
    const d = new Date(isoString);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return isoString;
  }
}

const pendingThClass =
  "px-3 py-2.5 text-left text-xs font-medium text-[#6d6d6d] border-b border-[#eee] bg-[#f8f8f8] whitespace-nowrap";
const pendingTdClass =
  "px-3 py-2 align-top text-xs leading-5 text-[#222] border-b border-[#f0f0f0]";

function PendingApplicationsTable({
  rows,
  openingLeadId,
  openingMarkKey,
  openedLeadId,
  onOpenRow,
  onOpenStatusMark,
}: {
  rows: NewApplicationListItem[];
  openingLeadId: string | null;
  openingMarkKey: string | null;
  openedLeadId: string | null;
  onOpenRow: (leadId: string) => void;
  onOpenStatusMark: (
    leadId: string,
    mark: NewApplicationListItemMark,
  ) => void;
}): JSX.Element {
  return (
    <div className="w-full rounded-md border border-[#eee] bg-white shadow-sm">
      <table className="w-full min-w-[880px] border-collapse text-left">
        <thead>
          <tr>
            <th className={pendingThClass}>Lead no.</th>
            <th className={`${pendingThClass} min-w-[120px]`}>Client</th>
            <th className={`${pendingThClass} min-w-[120px]`}>Mark</th>
            <th className={`${pendingThClass} min-w-[120px]`}>WhatsApp</th>
            <th className={`${pendingThClass} min-w-[160px]`}>Email</th>
            <th className={pendingThClass}>Stage</th>
            <th className={`${pendingThClass} whitespace-nowrap`}>Created</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isOpening = openingLeadId === row.id;
            const isOpened = openedLeadId === row.id;
            const rowClass = [
              "transition-colors",
              isOpening ? "opacity-60" : "",
              isOpened ? "bg-[#fff7e6] hover:bg-[#fff2d6]" : "",
              !isOpening && !isOpened
                ? "cursor-pointer hover:bg-[#fafafa] focus-visible:bg-[#fafafa] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#222]/30"
                : "",
              !isOpening && isOpened ? "cursor-pointer" : "",
            ].join(" ");

            const handleActivate = (): void => {
              if (!isOpening) onOpenRow(row.id);
            };

            const handleKeyDown = (
              e: React.KeyboardEvent<HTMLTableRowElement>
            ): void => {
              if (isOpening) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleActivate();
              }
            };

            return (
              <tr
                key={row.id}
                data-lead-id={row.id}
                tabIndex={isOpening ? undefined : 0}
                role="button"
                aria-label={`Open ${row.leadNo}`}
                aria-busy={isOpening || undefined}
                aria-current={isOpened ? "true" : undefined}
                className={rowClass}
                onClick={() => void handleActivate()}
                onKeyDown={handleKeyDown}
              >
                <td className={`${pendingTdClass} max-w-[120px]`}>
                  <span className="break-all">{row.leadNo}</span>
                </td>
                <td className={`${pendingTdClass} max-w-[180px]`}>
                  <span className="break-words">{row.clientName || "—"}</span>
                </td>
                <td className={`${pendingTdClass} max-w-[220px]`}>
                  {row.marks && row.marks.length > 0 ? (
                    <div className="flex flex-wrap items-center gap-1">
                      <span className="break-words font-medium">
                        {row.markName?.trim() || "—"}
                      </span>
                      {row.marks.map((m) => {
                        const label = formatStatusMarkChipLabel(m);
                        const markKey = `${row.id}::${m.applicationId}`;
                        const isLoading = openingMarkKey === markKey;
                        return (
                          <button
                            key={m.applicationId}
                            type="button"
                            disabled={isLoading}
                            aria-busy={isLoading || undefined}
                            title={
                              m.status ? `${label} — ${m.status}` : label
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenStatusMark(row.id, m);
                            }}
                            className="inline-flex items-center rounded-full border border-[#eee] bg-[#f8f8f8] px-2 py-0.5 text-[11px] leading-4 text-[#222] transition-colors hover:border-[#ddd] hover:bg-[#f0f0f0] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#222]/40 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="break-words">
                      {formatMarkDisplay(row)}
                    </span>
                  )}
                </td>
                <td className={`${pendingTdClass} max-w-[140px]`}>
                  <span className="break-all">{row.phone ?? "—"}</span>
                </td>
                <td className={`${pendingTdClass} max-w-[220px]`}>
                  <span className="break-all">{row.email ?? "—"}</span>
                </td>
                <td className={`${pendingTdClass} whitespace-nowrap`}>
                  {row.stage ?? "—"}
                </td>
                <td
                  className={`${pendingTdClass} whitespace-nowrap text-[11px] text-[#444]`}
                >
                  {formatCreatedAt(row.createdAt)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const VALID_MODULES: PendingItemsModule[] = [
  "NEW_APP",
  "FOLLOW_UP",
  "OPPOSITION",
  "ASSIGNMENT",
  "RENEWAL",
  "LEGAL_CERTIFICATE",
  "SURVEILLANCE",
];

export const DashboardPendingItemsView = (): JSX.Element => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter state lives in the URL so back-button + refresh restore the view.
  const selectedModule = useMemo<PendingItemsModule>(() => {
    const raw = (searchParams.get("module") ?? "").toUpperCase();
    return (VALID_MODULES as string[]).includes(raw)
      ? (raw as PendingItemsModule)
      : "NEW_APP";
  }, [searchParams]);
  const debouncedListSearch = searchParams.get("search")?.trim() ?? "";
  const page = Math.max(1, Number(searchParams.get("page")) || 1);
  const openedLeadId = searchParams.get("opened") ?? null;

  const [searchValue, setSearchValue] = useState("");
  const [listSearchInput, setListSearchInput] = useState(debouncedListSearch);
  const [items, setItems] = useState<NewApplicationListItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [openingMarkKey, setOpeningMarkKey] = useState<string | null>(null);

  const {
    stats,
    statsLoading,
    statsError,
    trackLoading,
    trackError,
    handleTrackRecord,
  } = useDashboardHome();

  const totalApplications = stats?.totalApplications ?? 0;
  const activeApplications = stats?.activeApplications ?? 0;
  const discussion = stats?.discussion ?? 0;
  const filing = stats?.filing ?? 0;
  const statusCount = stats?.status ?? 0;
  const registeredTms = stats?.registeredTms ?? 0;
  const pendingItemsBadge = discussion + filing + statusCount;
  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));

  /** Patch URL params with `replace` so dashboard filter changes don't pollute history. */
  const patchSearchParams = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      const next = new URLSearchParams(searchParams);
      mutate(next);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Sync the local search input when the URL changes externally (back/forward).
  useEffect(() => {
    setListSearchInput((current) =>
      current === debouncedListSearch ? current : debouncedListSearch
    );
  }, [debouncedListSearch]);

  // Debounce local input → URL search; also clears page + last-opened on change.
  useEffect(() => {
    const trimmed = listSearchInput.trim();
    if (trimmed === debouncedListSearch) return;
    const t = setTimeout(() => {
      patchSearchParams((next) => {
        if (trimmed) next.set("search", trimmed);
        else next.delete("search");
        next.delete("page");
        next.delete("opened");
      });
    }, 300);
    return () => clearTimeout(t);
  }, [listSearchInput, debouncedListSearch, patchSearchParams]);

  const handleModuleChange = (value: PendingItemsModule): void => {
    patchSearchParams((next) => {
      if (value === "NEW_APP") next.delete("module");
      else next.set("module", value);
      next.delete("page");
      next.delete("opened");
    });
  };

  const handlePageChange = (nextPage: number): void => {
    patchSearchParams((next) => {
      if (nextPage <= 1) next.delete("page");
      else next.set("page", String(nextPage));
      next.delete("opened");
    });
  };

  const setOpenedInUrl = useCallback(
    (leadId: string): void => {
      patchSearchParams((next) => {
        next.set("opened", leadId);
      });
    },
    [patchSearchParams]
  );

  const fetchList = useCallback(async () => {
    setListLoading(true);
    try {
      const search = debouncedListSearch || undefined;
      const data = await listPendingItemsByModule(
        selectedModule,
        page,
        PAGE_SIZE,
        search
      );
      setItems(data.items);
      setTotalRecords(data.total);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to load items"
      );
      setItems([]);
      setTotalRecords(0);
    } finally {
      setListLoading(false);
    }
  }, [page, debouncedListSearch, selectedModule]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  // After items load, scroll the previously-opened row into view (once per load).
  const scrolledForOpenedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!openedLeadId || items.length === 0) return;
    if (scrolledForOpenedRef.current === openedLeadId) return;
    const matches = items.some((it) => it.id === openedLeadId);
    if (!matches) return;
    const el = document.querySelector(
      `[data-lead-id="${openedLeadId}"]`
    ) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ behavior: "auto", block: "center" });
      scrolledForOpenedRef.current = openedLeadId;
    }
  }, [openedLeadId, items]);

  const handleSubmitTrack = async (): Promise<void> => {
    const result = await handleTrackRecord(searchValue);
    if (result !== null) {
      navigate("/dashboard/home/track-results", {
        state: { query: searchValue.trim(), result },
      });
    }
  };

  const handleOpenLead = async (leadId: string): Promise<void> => {
    setOpeningId(leadId);
    setOpenedInUrl(leadId);
    try {
      navigate(await getPathForLeadByModule(selectedModule, leadId));
    } finally {
      setOpeningId(null);
    }
  };

  const handleOpenStatusMark = async (
    leadId: string,
    mark: NewApplicationListItemMark
  ): Promise<void> => {
    const key = `${leadId}::${mark.applicationId}`;
    setOpeningMarkKey(key);
    setOpenedInUrl(leadId);
    try {
      navigate(await resolveStatusMarkPath(leadId, mark));
    } finally {
      setOpeningMarkKey(null);
    }
  };

  return (
    <DashboardLayout headerBrand="UpRight" headerTitle="Dashboard">
      <main className="min-h-0 flex-1 overflow-y-auto px-5 pb-6 pt-5 md:px-6">
        <div className="mx-auto min-h-0 w-full max-w-[1404px] rounded-lg bg-white p-6">
          <div className="flex w-full flex-col items-start gap-[22px]">
            <div className="flex w-full shrink-0 flex-col items-start gap-1">
              <h1 className="whitespace-nowrap text-2xl font-bold leading-9 text-[#333]">
                Trademark Management Suite
              </h1>
              <p className="max-w-[341px] text-sm font-normal leading-5 text-[#777]">
                Enterprise-grade trademark processing platform
              </p>
            </div>

            {statsError && (
              <p className="text-xs text-[#fb2c36]">{statsError}</p>
            )}

            <div className="flex w-full flex-col items-stretch gap-5 xl:flex-row">
              <StatCard
                title="Total Applications"
                value={statsLoading ? "—" : totalApplications}
                subtitle="+3 this month"
                icon={statTrend}
                iconAlt="Trend"
              />
              <StatCard
                title="Active Applications"
                value={statsLoading ? "—" : activeApplications}
                subtitle="+3 this month"
                icon={statTrend}
                iconAlt="Trend"
              />
              <StatCard
                title="Discussion"
                value={statsLoading ? "—" : discussion}
                subtitle="Awaiting action"
                icon={statDocument}
                iconAlt="Discussion"
              />
              <StatCard
                title="Filling"
                value={statsLoading ? "—" : filing}
                subtitle="Awaiting action"
                icon={statDocument}
                iconAlt="Filling"
              />
              <StatCard
                title="Status"
                value={statsLoading ? "—" : statusCount}
                subtitle="Awaiting action"
                icon={statDocument}
                iconAlt="Status"
              />
              <StatCard
                title="Registered TMs"
                value={statsLoading ? "—" : registeredTms}
                subtitle="Total portfolio"
                icon={statChart}
                iconAlt="Registered"
              />
            </div>

            <div className="flex w-full flex-col items-stretch gap-5 lg:flex-row">
              <ActionCard
                iconSlot={
                  <div className="flex size-12 items-center justify-center rounded-[10px] bg-[#314158]">
                    <HiOutlineSearch
                      className="h-6 w-6 shrink-0 text-white"
                      strokeWidth={1.5}
                    />
                  </div>
                }
                title="Track Status"
                subtitle="Monitor Application progress"
                variant="light"
                onClick={() => navigate("/dashboard/home")}
              />
              <ActionCard
                iconSlot={
                  <div className="flex size-12 items-center justify-center rounded-[10px] bg-white/20">
                    <HiOutlineClock
                      className="h-6 w-6 shrink-0 text-white"
                      strokeWidth={1.5}
                    />
                  </div>
                }
                title="Pending Items"
                subtitle="Review pending actions"
                variant="dark"
                badge={statsLoading ? undefined : pendingItemsBadge}
              />
              <ActionCard
                icon={actionReports}
                iconAlt="Generate reports"
                title="Generate Reports"
                subtitle="Analytics and insights"
                variant="light"
              />
              <ActionCard
                icon={actionUpdate}
                iconAlt="Update status"
                title="Update Status"
                subtitle="Update Status"
                variant="light"
                onClick={() => navigate("/dashboard/home/update-status")}
              />
            </div>

            <div className="flex w-full max-w-full flex-col items-start gap-1">
              <label
                className="w-full text-xs font-normal leading-5 text-[#222]"
                htmlFor="pending-track-input"
              >
                Mark or Application Number
              </label>
              <div className="flex w-full items-center rounded-md border border-[#eee] bg-white px-[10px] py-2">
                <input
                  id="pending-track-input"
                  type="text"
                  className="bg-transparent w-full flex-1 text-xs font-normal leading-5 text-[#222] outline-none placeholder:text-[#6d6d6d]"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void handleSubmitTrack();
                  }}
                  placeholder="Text Here"
                />
              </div>
            </div>

            <div>
              <button
                type="button"
                onClick={() => void handleSubmitTrack()}
                disabled={trackLoading || !searchValue.trim()}
                className="inline-flex h-12 min-h-12 w-full items-center justify-center rounded-lg bg-[#222] px-4 text-center text-sm font-medium leading-5 text-white transition-colors hover:bg-[#333] disabled:cursor-not-allowed disabled:opacity-50 sm:w-[251px] sm:px-6 sm:text-base sm:leading-6"
              >
                {trackLoading ? "Searching…" : "Track Record"}
              </button>
            </div>

            {trackError && (
              <p className="text-xs text-[#fb2c36]">{trackError}</p>
            )}

            <div className="flex w-full max-w-full flex-col items-start gap-2">
              <h2 className="text-sm font-medium leading-5 text-[#222]">
                {PENDING_ITEMS_MODULE_OPTIONS.find(
                  (o) => o.value === selectedModule
                )?.label ?? "Items"}
              </h2>
              <div className="flex w-full flex-col items-stretch gap-2 sm:flex-row sm:items-center">
                <div className="flex w-full max-w-xl items-center gap-2 rounded-md border border-[#eee] bg-white px-[10px] py-2">
                  <HiOutlineSearch
                    className="h-4 w-4 shrink-0 text-[#6d6d6d]"
                    strokeWidth={2}
                    aria-hidden
                  />
                  <input
                    type="search"
                    value={listSearchInput}
                    onChange={(e) => setListSearchInput(e.target.value)}
                    placeholder="Search items…"
                    className="bg-transparent min-w-0 flex-1 text-xs font-normal leading-5 text-[#222] outline-none placeholder:text-[#6d6d6d]"
                    aria-label="Search items list"
                  />
                </div>
                <Select
                  value={selectedModule}
                  onValueChange={(v) => handleModuleChange(v as PendingItemsModule)}
                >
                  <SelectTrigger
                    aria-label="Filter by module"
                    className="h-[38px] w-full text-xs font-normal leading-5 sm:w-[200px]"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PENDING_ITEMS_MODULE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="max-h-[min(520px,58vh)] min-h-0 w-full overflow-auto pr-0.5">
              {listLoading ? (
                <p className="shrink-0 py-1 text-xs leading-5 text-[#62748e]">
                  Loading items…
                </p>
              ) : items.length === 0 ? (
                <p className="shrink-0 py-1 text-xs leading-5 text-[#62748e]">
                  {debouncedListSearch
                    ? "No items match your search."
                    : "No items to show."}
                </p>
              ) : (
                <PendingApplicationsTable
                  rows={items}
                  openingLeadId={openingId}
                  openingMarkKey={openingMarkKey}
                  openedLeadId={openedLeadId}
                  onOpenRow={(leadId) => void handleOpenLead(leadId)}
                  onOpenStatusMark={(leadId, mark) =>
                    void handleOpenStatusMark(leadId, mark)
                  }
                />
              )}
            </div>

            {!listLoading && totalRecords > PAGE_SIZE && (
              <div className="flex w-full flex-row flex-wrap items-center justify-end gap-6">
                <span className="text-xs font-normal leading-5 text-[#6d6d6d]">
                  Showing {(page - 1) * PAGE_SIZE + 1} to{" "}
                  {Math.min(page * PAGE_SIZE, totalRecords)} of {totalRecords}{" "}
                  records
                </span>
                <div className="flex flex-row items-center gap-3">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => handlePageChange(Math.max(1, page - 1))}
                    className="flex h-7 w-7 items-center justify-center rounded-md border border-[#eee] bg-white hover:bg-[#f7f7f7] disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label="Previous page"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                    className="flex h-7 w-7 items-center justify-center rounded-md border border-[#eee] bg-white hover:bg-[#f7f7f7] disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label="Next page"
                  >
                    ›
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </DashboardLayout>
  );
};
